import awsS3Service from './awsS3Service';
import excelAwsS3Service, { ExcelWorkbook, ExcelSheet } from './excelAwsS3Service';

/**
 * Service to manage consolidated user workbooks
 * Each user gets ONE workbook with multiple sheets (one per admin assignment)
 */
class UserConsolidatedWorkbookService {
  private readonly S3_PREFIX = 'user-workbooks';
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private cache: Map<string, { data: ExcelWorkbook; timestamp: number }> = new Map();

  /**
   * Get or create a consolidated workbook for a user
   */
  async getUserWorkbook(userId: string, userName: string): Promise<ExcelWorkbook> {
    try {
      console.log(`📚 Getting consolidated workbook for user: ${userId} (${userName})`);

      // Check cache first
      const cacheKey = `user_workbook_${userId}`;
      const cached = this.cache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
        console.log('⚡ Using cached user workbook');
        return cached.data;
      }

      // Try to load existing workbook from AWS S3
      const s3Key = `${this.S3_PREFIX}/${userId}/consolidated_workbook.json`;
      
      if (awsS3Service.isConfigured()) {
        try {
          const fileContent = await awsS3Service.downloadFile('ettaskmanager', s3Key);
          const workbook: ExcelWorkbook = JSON.parse(fileContent);
          
          // Cache the workbook
          this.cache.set(cacheKey, { data: workbook, timestamp: Date.now() });
          
          console.log(`✅ Loaded existing workbook with ${workbook.sheets.length} sheets`);
          return workbook;
        } catch (error) {
          console.log('📝 No existing workbook found, creating new one');
        }
      }

      // Create new workbook if none exists
      const newWorkbook: ExcelWorkbook = {
        id: `user_workbook_${userId}`,
        folderId: userId,
        workbookName: `${userName}'s Tasks`,
        sheets: [],
        columnWidths: {},
        rowHeights: {},
        version: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: userId,
        updatedBy: userId,
        s3Key: s3Key,
        s3Bucket: 'ettaskmanager'
      };

      // Cache the new workbook
      this.cache.set(cacheKey, { data: newWorkbook, timestamp: Date.now() });

      console.log('✅ Created new consolidated workbook');
      return newWorkbook;

    } catch (error) {
      console.error('❌ Error getting user workbook:', error);
      throw error;
    }
  }

  /**
   * Add or update a sheet in user's consolidated workbook
   */
  async addOrUpdateSheet(
    userId: string,
    userName: string,
    sheetName: string,
    taskData: {
      headers: string[];
      row: string[];
      workbookId: string;
      assignedBy: string;
    }
  ): Promise<{ success: boolean; workbook: ExcelWorkbook }> {
    try {
      console.log(`📝 Adding/updating sheet "${sheetName}" for user ${userName}`);

      // Get or create user's consolidated workbook
      const workbook = await this.getUserWorkbook(userId, userName);

      // Find existing sheet or create new one
      let targetSheet = workbook.sheets.find(s => s.name === sheetName);
      
      if (!targetSheet) {
        console.log(`➕ Creating new sheet: ${sheetName}`);
        
        // Create header row
        const headerRow = taskData.headers.map(header => ({
          value: header,
          format: { bold: true, backgroundColor: '#f0f0f0', textAlign: 'center' as const }
        }));

        // Create data row
        const dataRow = taskData.row.map(value => ({
          value: value,
          format: {}
        }));

        targetSheet = {
          name: sheetName,
          data: [headerRow, dataRow],
          selectedCell: { row: 0, col: 0 },
          selectedRange: null
        };

        workbook.sheets.push(targetSheet);
        console.log(`✅ New sheet "${sheetName}" created with 1 task`);
      } else {
        console.log(`📊 Updating existing sheet: ${sheetName}`);
        
        // Add new row to existing sheet
        const dataRow = taskData.row.map(value => ({
          value: value,
          format: {}
        }));

        targetSheet.data.push(dataRow);
        console.log(`✅ Added task to sheet "${sheetName}" (now has ${targetSheet.data.length - 1} tasks)`);
      }

      // Update workbook metadata
      workbook.updatedAt = new Date().toISOString();
      workbook.version += 1;

      // Save to AWS S3
      await this.saveUserWorkbook(userId, workbook);

      console.log(`✅ Consolidated workbook saved with ${workbook.sheets.length} sheets`);

      return { success: true, workbook };

    } catch (error) {
      console.error('❌ Error adding/updating sheet:', error);
      return { success: false, workbook: await this.getUserWorkbook(userId, userName) };
    }
  }

  /**
   * Save consolidated workbook to AWS S3
   */
  private async saveUserWorkbook(userId: string, workbook: ExcelWorkbook): Promise<void> {
    try {
      if (!awsS3Service.isConfigured()) {
        console.warn('⚠️ AWS S3 not configured, cannot save user workbook');
        return;
      }

      const s3Key = `${this.S3_PREFIX}/${userId}/consolidated_workbook.json`;
      const workbookData = JSON.stringify(workbook, null, 2);

      await awsS3Service.uploadStringContent(
        'ettaskmanager',
        s3Key,
        workbookData,
        'application/json'
      );

      // Update cache
      const cacheKey = `user_workbook_${userId}`;
      this.cache.set(cacheKey, { data: workbook, timestamp: Date.now() });

      console.log(`💾 User workbook saved to AWS S3: ${s3Key}`);

    } catch (error) {
      console.error('❌ Error saving user workbook:', error);
      throw error;
    }
  }

  /**
   * Delete a sheet from user's consolidated workbook
   */
  async deleteSheet(userId: string, userName: string, sheetName: string): Promise<boolean> {
    try {
      const workbook = await this.getUserWorkbook(userId, userName);
      const sheetIndex = workbook.sheets.findIndex(s => s.name === sheetName);

      if (sheetIndex === -1) {
        console.warn(`⚠️ Sheet "${sheetName}" not found`);
        return false;
      }

      workbook.sheets.splice(sheetIndex, 1);
      workbook.updatedAt = new Date().toISOString();
      workbook.version += 1;

      await this.saveUserWorkbook(userId, workbook);

      console.log(`🗑️ Sheet "${sheetName}" deleted from user workbook`);
      return true;

    } catch (error) {
      console.error('❌ Error deleting sheet:', error);
      return false;
    }
  }

  /**
   * Get all sheets in user's workbook
   */
  async getUserSheets(userId: string, userName: string): Promise<ExcelSheet[]> {
    try {
      const workbook = await this.getUserWorkbook(userId, userName);
      return workbook.sheets;
    } catch (error) {
      console.error('❌ Error getting user sheets:', error);
      return [];
    }
  }

  /**
   * Clear cache for a user
   */
  clearCache(userId: string): void {
    const cacheKey = `user_workbook_${userId}`;
    this.cache.delete(cacheKey);
    console.log(`🗑️ Cache cleared for user: ${userId}`);
  }
}

// Export singleton instance
export const userConsolidatedWorkbookService = new UserConsolidatedWorkbookService();
export default userConsolidatedWorkbookService;








